import React, { useCallback, useRef, useState } from 'react'
import GrantContent from '../../components/GrantContent'
import * as Permisos from '../../utils/Permisos'
import { AgencyIcon, ChevronDownIcon, LogoutIcon, PlanIcon, ProfileIcon, SettingsIcon } from './icons'
import KarteraThemeSwitch from './KarteraThemeSwitch'
import useDismiss from './useDismiss'

const menuItemClass = 'flex min-h-10 w-full cursor-pointer items-center gap-2.5 rounded-[10px] border-0 bg-transparent px-2.5 text-left text-sm font-semibold hover:bg-k-hover'

const KarteraAccountMenu = ({ user, onNavigate, onLogout }) => {
    const ref = useRef(null)
    const [open, setOpen] = useState(false)
    const close = useCallback(() => setOpen(false), [])
    useDismiss(ref, open, close)

    const initials = `${user.nombres?.charAt(0) || ''}${user.apPat?.charAt(0) || ''}`.toUpperCase() || 'AG'
    const fullName = [user.nombres, user.apPat].filter(Boolean).join(' ') || 'Agente'
    const plan = user.suscripcion?.plan?.nombre
    const clave = user.agenteId ? String(user.agenteId).padStart(4, '0') : '0000'

    const go = (to) => {
        setOpen(false)
        onNavigate(to)
    }

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                aria-label="Menú de la cuenta"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
                className={`flex min-h-11 cursor-pointer items-center gap-2.5 rounded-full border-0 py-1 pl-1 pr-1 md:pr-2.5 ${open ? 'bg-k-accent-soft' : 'bg-transparent hover:bg-k-hover'}`}
            >
                <span className="relative flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#3E6BE6,#8B3FC9_55%,#EC3B6C)] text-sm font-extrabold text-white">
                    {initials}
                    <span className="absolute -bottom-px -right-px h-[11px] w-[11px] rounded-full border-2 border-k-surface bg-brand-teal" />
                </span>
                <span className="hidden min-w-0 flex-col text-left leading-[1.25] md:flex">
                    <span className="max-w-[180px] truncate text-sm font-bold text-k-ink">{fullName}</span>
                    <span className="max-w-[180px] truncate text-xs text-k-muted">Agente · Clave {clave}</span>
                </span>
                <ChevronDownIcon className="hidden shrink-0 text-k-muted md:block" />
            </button>

            {open && (
                <div role="menu" className="absolute right-0 top-[54px] z-30 flex w-[260px] max-w-[calc(100vw-24px)] flex-col rounded-2xl border border-k-line bg-k-surface p-2 text-k-ink shadow-k-pop">
                    <div className="mb-1.5 flex flex-col gap-0.5 border-b border-k-line-soft px-2.5 pb-3 pt-2.5">
                        <span className="truncate text-sm font-bold">{fullName}</span>
                        <span className="truncate text-[13px] text-k-muted">{user.correo}</span>
                        {plan && (
                            <span className="mt-1.5 self-start rounded-full bg-k-accent-soft px-2 py-0.5 text-xs font-bold text-k-accent-ink">{plan}</span>
                        )}
                    </div>
                    <button type="button" role="menuitem" className={menuItemClass} onClick={() => go('/configuracion/mi-cuenta')}>
                        <ProfileIcon />Mi perfil
                    </button>
                    {user.suscripcion && (
                        <button type="button" role="menuitem" className={menuItemClass} onClick={() => go('/configuracion/mi-plan')}>
                            <PlanIcon />Mi plan
                        </button>
                    )}
                    <button type="button" role="menuitem" className={menuItemClass} onClick={() => go('/configuracion')}>
                        <SettingsIcon />Configuración
                    </button>
                    <div className="px-1 py-1 md:hidden">
                        <KarteraThemeSwitch showLabels className="w-full" />
                    </div>
                    <span aria-hidden="true" className="my-1.5 h-px bg-k-line-soft" />
                    <button type="button" role="menuitem" className={`${menuItemClass} text-k-danger`} onClick={onLogout}>
                        <LogoutIcon />Cerrar sesión
                    </button>
                </div>
            )}
        </div>
    )
}

export default KarteraAccountMenu
